<template>
  <div class="card mb-4">
    <div class="card-header pb-0">
      <h6>Categorias</h6>
      <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <p class="text-sm mb-0">
          <span class="font-weight-bold">{{ categorias.length }}</span> categorias encontradas
        </p>
        <div class="d-flex flex-wrap gap-2">
          <button 
            @click="showAddModal"
            class="btn btn-sm btn-custom-gradient"
          >
            Adicionar
          </button>
          <button 
            @click="loadCategorias" 
            class="btn btn-sm"
            :class="loading ? 'btn-secondary' : 'btn-custom-gradient'"
            :disabled="loading"
          >
           {{ loading ? 'Carregando...' : 'Atualizar' }}
         </button>
        </div>
      </div>
    </div>
    <div class="card-body px-0 pt-0 pb-2">
      <div v-if="error" class="alert alert-danger mx-3">
        {{ error }}
      </div>
      <div v-if="loading" class="text-center py-4">
        <div class="spinner-border spinner-teal" role="status">
          <span class="visually-hidden">Carregando...</span>
        </div>
      </div>
      <div v-else class="table-responsive p-0">
        <table class="table align-items-center mb-0">
          <thead>
            <tr>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                Nome
              </th>
              <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                Cesta Básica
              </th>
              <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                Status
              </th>
              <th class="text-secondary opacity-7"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="categoria in categorias" :key="categoria.id">
              <td>
                <div class="d-flex px-2 py-1">
                  <div class="d-flex flex-column justify-content-center">
                    <h6 class="mb-0 text-sm">{{ categoria.nome || categoria.name }}</h6>
                    <p class="text-xs text-secondary mb-0">
                      ID: {{ categoria.id }}
                    </p>
                  </div>
                </div>
              </td>
              <td class="align-middle text-center">
                <soft-badge 
                  :color="getCestaBasicaColor(categoria.basic_food_basket)" 
                  variant="gradient" 
                  size="sm"
                >
                  {{ getCestaBasicaText(categoria.basic_food_basket) }}
                </soft-badge>
              </td>
              <td class="align-middle text-center text-sm">
                <soft-badge 
                  :color="getStatusColor(categoria.status || categoria.ativo)" 
                  variant="gradient" 
                  size="sm"
                >
                  {{ getStatusText(categoria.status || categoria.ativo) }}
                </soft-badge>
              </td>
              <td class="align-middle">
                <div class="d-flex align-items-center justify-content-end gap-2 pe-3">
                  <a
                    href="javascript:;"
                    class="text-secondary font-weight-bold text-xs"
                    data-toggle="tooltip"
                    data-original-title="Ver detalhes"
                    @click="viewDetails(categoria)"
                  >
                    Detalhes
                  </a>
                  <a
                    href="javascript:;"
                    class="text-info"
                    data-toggle="tooltip"
                    data-original-title="Editar categoria"
                    @click="editCategory(categoria)"
                  >
                    <i class="fas fa-edit"></i>
                  </a>
                  <a
                    href="javascript:;"
                    class="text-danger"
                    data-toggle="tooltip"
                    data-original-title="Excluir categoria"
                    @click="deleteCategory(categoria)"
                  >
                    <i class="fas fa-trash-alt"></i>
                  </a>
                </div>
              </td>
            </tr>
            <tr v-if="categorias.length === 0 && !loading">
              <td colspan="5" class="text-center py-4">
                <p class="text-secondary">Nenhuma categoria encontrada</p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Modal de Adicionar Categoria -->
    <div v-if="showAddCategoryModal" class="modal-backdrop fade show" @click="closeAddModal" style="z-index: 9999;"></div>
    <div v-if="showAddCategoryModal" class="modal fade show d-block" tabindex="-1" aria-labelledby="addCategoryModalLabel" aria-hidden="true" style="z-index: 10000;">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="addCategoryModalLabel">
              <i class="fas fa-plus me-2"></i>
              Adicionar Nova Categoria
            </h5>
            <button type="button" class="btn-close" @click="closeAddModal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div v-if="loadingAdd" class="text-center py-4">
              <div class="spinner-border spinner-teal" role="status">
                <span class="visually-hidden">Carregando...</span>
              </div>
              <p class="mt-2">Cadastrando categoria...</p>
            </div>
            <div v-else-if="addError" class="alert alert-danger">
              {{ addError }}
            </div>
            <div v-else>
              <form @submit.prevent="addCategory">
                <div class="row">
                  <div class="col-12 mb-3">
                    <label for="categoryName" class="form-label">Nome da Categoria *</label>
                    <input 
                      type="text" 
                      class="form-control" 
                      id="categoryName"
                      v-model="newCategory.name"
                      placeholder="Ex: Leites e derivados"
                      required
                    >
                  </div>
                  <div class="col-12 mb-3">
                    <label for="categoryBasicFoodBasket" class="form-label">Cesta Básica *</label>
                    <select 
                      class="form-control" 
                      id="categoryBasicFoodBasket"
                      v-model="newCategory.basic_food_basket"
                      required
                    >
                      <option value="">Selecione uma opção</option>
                      <option value="S">Sim</option>
                      <option value="N">Não</option>
                    </select>
                  </div>
                </div>
              </form>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeAddModal">Cancelar</button>
            <button 
              type="button" 
              class="btn btn-custom-gradient" 
              @click="addCategory"
              :disabled="loadingAdd || !newCategory.name || !newCategory.basic_food_basket"
            >
              <i class="fas fa-save me-1"></i>
              Cadastrar Categoria
            </button>
          </div>
        </div>
    </div>
  </div>

    <!-- Modal de Editar Categoria -->
    <div v-if="showEditCategoryModal" class="modal-backdrop fade show" @click="closeEditModal" style="z-index: 9999;"></div>
    <div v-if="showEditCategoryModal" class="modal fade show d-block" tabindex="-1" aria-labelledby="editCategoryModalLabel" aria-hidden="true" style="z-index: 10000;">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="editCategoryModalLabel">
              <i class="fas fa-edit me-2"></i>
              Editar Categoria
            </h5>
            <button type="button" class="btn-close" @click="closeEditModal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div v-if="loadingEdit" class="text-center py-4">
              <div class="spinner-border spinner-teal" role="status">
                <span class="visually-hidden">Carregando...</span>
              </div>
              <p class="mt-2">Atualizando categoria...</p>
            </div>
            <div v-else-if="editError" class="alert alert-danger">
              {{ editError }}
            </div>
            <div v-else>
              <form @submit.prevent="updateCategory">
                <div class="row">
                  <div class="col-12 mb-3">
                    <label for="editCategoryName" class="form-label">Nome da Categoria *</label>
                    <input 
                      type="text" 
                      class="form-control" 
                      id="editCategoryName"
                      v-model="editCategoryData.name"
                      placeholder="Ex: Leites e derivados"
                      required
                    >
                  </div>
                  <div class="col-12 mb-3">
                    <label for="editCategoryBasicFoodBasket" class="form-label">Cesta Básica *</label>
                    <select 
                      class="form-control" 
                      id="editCategoryBasicFoodBasket"
                      v-model="editCategoryData.basic_food_basket"
                      required
                    >
                      <option value="">Selecione uma opção</option>
                      <option value="S">Sim</option>
                      <option value="N">Não</option>
                    </select>
                  </div>
                </div>
              </form>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeEditModal">Cancelar</button>
            <button 
              type="button" 
              class="btn btn-custom-gradient" 
              @click="updateCategory"
              :disabled="loadingEdit || !editCategoryData.name || !editCategoryData.basic_food_basket"
            >
              <i class="fas fa-save me-1"></i>
              Salvar Alterações
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Detalhes da Categoria -->
    <div v-if="showDetailsModal" class="modal-backdrop fade show" @click="closeDetailsModal" style="z-index: 9999;"></div>
    <div v-if="showDetailsModal" class="modal fade show d-block" tabindex="-1" aria-labelledby="categoryDetailsModalLabel" aria-hidden="true" style="z-index: 10000;">
      <div class="modal-dialog modal-dialog-centered category-details-modal">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="categoryDetailsModalLabel">
              <i class="fas fa-info-circle me-2"></i>
              Detalhes da Categoria
            </h5>
            <button type="button" class="btn-close" @click="closeDetailsModal" aria-label="Close"></button>
          </div>
          <div class="modal-body p-4">
            <div v-if="loadingDetails" class="text-center py-4">
              <div class="spinner-border spinner-teal" role="status">
                <span class="visually-hidden">Carregando...</span>
              </div>
              <p class="mt-2">Carregando detalhes da categoria...</p>
            </div>
            <div v-else-if="selectedCategory" class="row">
              <div class="col-md-6">
                <div class="category-icon-container text-center mb-3">
                  <div class="category-icon-placeholder">
                    <i class="fas fa-tags fa-5x text-info"></i>
                    <p class="text-muted mt-2">Categoria</p>
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="category-details ps-3">
                  <h6 class="text-primary mb-4">{{ selectedCategory.nome || selectedCategory.name || 'Sem nome' }}</h6>
                  
                  <div class="detail-item mb-4">
                    <strong>ID:</strong> {{ selectedCategory.id }}
                  </div>
                  
                  <div class="detail-item mb-4" v-if="selectedCategory.description">
                    <strong>Descrição:</strong>
                    <p class="text-muted mt-2 mb-0">
                      {{ selectedCategory.description }}
                    </p>
                  </div>
                  
                  <div class="detail-item mb-4">
                    <strong>Cesta Básica:</strong>
                    <soft-badge 
                      :color="getCestaBasicaColor(selectedCategory.basic_food_basket)" 
                      variant="gradient" 
                      size="sm"
                      class="ms-2"
                    >
                      {{ getCestaBasicaText(selectedCategory.basic_food_basket) }}
                    </soft-badge>
                  </div>
                  
                  <div class="detail-item mb-4">
                    <strong>Status:</strong>
                    <soft-badge 
                      :color="getStatusColor(selectedCategory.status || selectedCategory.ativo)" 
                      variant="gradient" 
                      size="sm"
                      class="ms-2"
                    >
                      {{ getStatusText(selectedCategory.status || selectedCategory.ativo) }}
                    </soft-badge>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="text-center py-4">
              <div class="alert alert-danger">
                <i class="fas fa-exclamation-triangle me-2"></i>
                Erro ao carregar detalhes da categoria
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeDetailsModal">Fechar</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SoftBadge from "@/components/SoftBadge.vue";
import { API_ENDPOINTS, apiRequest, handleApiResponse } from "@/config/api.js";
import { showSuccessAlert, showErrorAlert, showConfirmAlert } from "@/utils/swal.js";

export default {
  name: "categorias-table",
  data() {
    return {
      categorias: [],
      loading: false,
      error: null,
      showAddCategoryModal: false,
      showEditCategoryModal: false,
      loadingAdd: false,
      loadingEdit: false,
      addError: null,
      editError: null,
      showDetailsModal: false,
      selectedCategory: null,
      editingCategory: null,
      loadingDetails: false,
      newCategory: {
        name: '',
        basic_food_basket: ''
      },
      editCategoryData: {
        name: '',
        basic_food_basket: ''
      }
    };
  },
  components: {
    SoftBadge,
  },
  mounted() {
    this.loadCategorias();
  },
  methods: {
    async loadCategorias() {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await apiRequest(API_ENDPOINTS.CATEGORY, {
          method: 'GET'
        });
        
        const data = await handleApiResponse(response);
        this.categorias = Array.isArray(data) ? data : (data.data || data.categorias || []);
        
        console.log('Categorias carregadas:', this.categorias);
        
      } catch (error) {
        console.error('Erro ao carregar categorias:', error);
        this.error = error.message || 'Erro ao carregar categorias';
      } finally {
        this.loading = false;
      }
    },
    
    getStatusColor(status) {
      if (status === true || status === 'ativo' || status === 'active') {
        return 'success';
      } else if (status === false || status === 'inativo' || status === 'inactive') {
        return 'secondary';
      }
      return 'info';
    },
    
    getStatusText(status) {
      if (status === true || status === 'ativo' || status === 'active') {
        return 'Ativo';
      } else if (status === false || status === 'inativo' || status === 'inactive') {
        return 'Inativo';
      }
      return 'Desconhecido';
    },

    getCestaBasicaColor(basicFoodBasket) {
      if (basicFoodBasket === 'S' || basicFoodBasket === true) {
        return 'success';
      } else if (basicFoodBasket === 'N' || basicFoodBasket === false) {
        return 'info';
      }
      return 'secondary';
    },
    
    getCestaBasicaText(basicFoodBasket) {
      if (basicFoodBasket === 'S' || basicFoodBasket === true) {
        return 'Sim';
      } else if (basicFoodBasket === 'N' || basicFoodBasket === false) {
        return 'Não';
      }
      return 'N/A';
    },
    
    async viewDetails(categoria) {
      this.showDetailsModal = true;
      this.selectedCategory = categoria;
      this.loadingDetails = true;
      
      try {
        // Busca os detalhes completos da categoria da API
        const response = await apiRequest(`${API_ENDPOINTS.CATEGORY}/${categoria.id}`, {
          method: 'GET'
        });
        
        const data = await handleApiResponse(response);
        this.selectedCategory = data;
        
        console.log('Detalhes da categoria carregados:', data);
        
      } catch (error) {
        console.error('Erro ao carregar detalhes da categoria:', error);
        // Se falhar, usa os dados da lista como fallback
        this.selectedCategory = categoria;
      } finally {
        this.loadingDetails = false;
      }
    },
    
    closeDetailsModal() {
      this.showDetailsModal = false;
      this.selectedCategory = null;
      this.loadingDetails = false;
    },

    showAddModal() {
      this.showAddCategoryModal = true;
      this.addError = null;
      this.newCategory = {
        name: '',
        basic_food_basket: ''
      };
    },

    closeAddModal() {
      this.showAddCategoryModal = false;
      this.addError = null;
      this.newCategory = {
        name: '',
        basic_food_basket: ''
      };
    },

    async addCategory() {
      if (!this.newCategory.name || !this.newCategory.basic_food_basket) {
        this.addError = 'Todos os campos são obrigatórios';
        return;
      }

      this.loadingAdd = true;
      this.addError = null;

      try {
        const categoryPayload = {
          name: this.newCategory.name,
          basic_food_basket: this.newCategory.basic_food_basket
        };

        const response = await apiRequest(API_ENDPOINTS.CATEGORY, {
          method: 'POST',
          body: JSON.stringify(categoryPayload)
        });

        await handleApiResponse(response);

        // Sucesso! Fecha o modal e recarrega a lista
        this.closeAddModal();
        this.loadCategorias();
        
        // Mostra mensagem de sucesso com SweetAlert
        await showSuccessAlert(
          'Categoria cadastrada com sucesso!',
          'A categoria foi adicionada ao sistema.',
          3000
        );

      } catch (error) {
        console.error('Erro ao cadastrar categoria:', error);
        this.addError = error.message || 'Erro ao cadastrar categoria';
      } finally {
        this.loadingAdd = false;
      }
    },

    editCategory(categoria) {
      this.editingCategory = categoria;
      this.editCategoryData = {
        name: categoria.nome || categoria.name || '',
        basic_food_basket: categoria.basic_food_basket || ''
      };
      this.editError = null;
      this.showEditCategoryModal = true;
    },

    closeEditModal() {
      this.showEditCategoryModal = false;
      this.editingCategory = null;
      this.editError = null;
      this.editCategoryData = {
        name: '',
        basic_food_basket: ''
      };
    },

    async updateCategory() {
      if (!this.editCategoryData.name || !this.editCategoryData.basic_food_basket) {
        this.editError = 'Todos os campos são obrigatórios';
        return;
      }

      this.loadingEdit = true;
      this.editError = null;

      try {
        const categoryPayload = {
          name: this.editCategoryData.name,
          basic_food_basket: this.editCategoryData.basic_food_basket
        };

        const response = await apiRequest(`${API_ENDPOINTS.CATEGORY}/${this.editingCategory.id}`, {
          method: 'PUT',
          body: JSON.stringify(categoryPayload)
        });

        await handleApiResponse(response);

        // Sucesso! Fecha o modal e recarrega a lista
        this.closeEditModal();
        this.loadCategorias();
        
        // Mostra mensagem de sucesso com SweetAlert
        await showSuccessAlert(
          'Categoria atualizada com sucesso!',
          'A categoria foi atualizada no sistema.',
          3000
        );

      } catch (error) {
        console.error('Erro ao atualizar categoria:', error);
        this.editError = error.message || 'Erro ao atualizar categoria';
      } finally {
        this.loadingEdit = false;
      }
    },

    async deleteCategory(categoria) {
      // Mostra confirmação antes de excluir
      const result = await showConfirmAlert(
        'Excluir Categoria',
        `Tem certeza que deseja excluir a categoria "${categoria.nome || categoria.name}"? Esta ação não pode ser desfeita.`,
        'Sim, excluir',
        'Cancelar'
      );

      if (!result.isConfirmed) {
        return;
      }

      try {
        const response = await apiRequest(`${API_ENDPOINTS.CATEGORY}/${categoria.id}`, {
          method: 'DELETE'
        });

        await handleApiResponse(response);

        // Sucesso! Recarrega a lista
        this.loadCategorias();
        
        // Mostra mensagem de sucesso com SweetAlert
        await showSuccessAlert(
          'Categoria excluída com sucesso!',
          'A categoria foi removida do sistema.',
          3000
        );

      } catch (error) {
        console.error('Erro ao excluir categoria:', error);
        await showErrorAlert(
          'Erro ao excluir categoria',
          error.message || 'Não foi possível excluir a categoria.'
        );
      }
    }
  },
};
</script>

<style scoped>
.modal-backdrop {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  background-color: rgba(0, 0, 0, 0.5) !important;
}

.modal {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.modal-dialog {
  width: 100%;
  margin: 0.5rem auto;
}

@media (max-width: 576px) {
  .modal-dialog {
    max-width: calc(100% - 1rem);
    margin: 0.5rem;
  }
}

.btn-custom-gradient {
  background: linear-gradient(310deg, #17c1e8 0%, #20c997 100%);
  border: none;
  color: white;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-custom-gradient:hover {
  background: linear-gradient(310deg, #15a8d1 0%, #1bb88a 100%);
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(23, 193, 232, 0.3);
}

.btn-custom-gradient:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(23, 193, 232, 0.3);
}

.spinner-teal {
  color: #20c997 !important;
}

.gap-2 {
  gap: 0.5rem;
}

.text-info:hover,
.text-danger:hover {
  opacity: 0.8;
  transition: opacity 0.2s ease;
  cursor: pointer;
}

.text-info,
.text-danger {
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.category-icon-placeholder {
  padding: 40px 20px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 2px dashed #dee2e6;
}

.category-details {
  padding: 0 10px;
}

.detail-item {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 12px;
}

.detail-item:last-child {
  border-bottom: none;
}

.modal-dialog.modal-lg {
  max-height: 80vh;
}

.modal-dialog.modal-lg .modal-content {
  max-height: 80vh;
  overflow-y: auto;
}

.modal-dialog.modal-lg .modal-body {
  max-height: 60vh;
  overflow-y: auto;
}

.category-details-modal {
  max-width: 75% !important;
  width: 75% !important;
}

@media (min-width: 1200px) {
  .category-details-modal {
    max-width: 900px !important;
    width: 900px !important;
  }
}
</style>
